import { FC } from 'react';

import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDeleteTemplateMutation } from 'apis/report-templates.api';
import { DAY_FORMAT_WITH_SLASHES } from 'constants/global';
import { useOpen } from 'hooks/useOpen';
import { ReportTemplateItem } from 'interfaces/report-templates.interface';
import { CommonError } from 'interfaces/shared.interface';
import { TableData, TableRow, DropdownMenu, ErrorModal, WarningModal } from 'shared-components';
import { successNotify } from 'utils/notifications';
import { getReportTemplateRunLink, getReportTemplateEditLink } from 'utils/url';

import GenerateTemplateModal from './GenerateTemplateModal';

interface Props {
  reportTemplatesData: ReportTemplateItem;
  refetchTemplates: VoidFunction;
}

const ReportTemplatesRow: FC<Props> = ({ reportTemplatesData, refetchTemplates }) => {
  const navigation = useNavigate();
  const { name, visualizationType, creatorName, creationTime, id } = reportTemplatesData;
  const [isOpenGenerateTemplateModal, handleOpenGenerateTemplateModal, handleCloseGenerateTemplateModal] = useOpen();
  const [isOpenInfoModal, handleOpenInfoModal, handleCloseInfoModal] = useOpen();
  const [isErrorModalOpen, handleOpenErrorModal, handleCloseErrorModal] = useOpen();

  const [deleteTemplate, { error: deleteTemplateError }] = useDeleteTemplateMutation();

  const { t } = useTranslation();

  const errorData = (deleteTemplateError as CommonError)?.data || {};

  const menuItemsAction = [
    {
      dataCy: 'menu-item-generate-button',
      name: 'Generate',
      actionOnClick: handleOpenGenerateTemplateModal,
    },
    {
      dataCy: 'menu-item-run-button',
      name: 'Run',
      actionOnClick: () => {
        navigation(getReportTemplateRunLink(String(id)));
      },
    },
    {
      dataCy: 'menu-item-edit-button',
      name: 'Edit',
      actionOnClick: () => {
        navigation(getReportTemplateEditLink(String(id)));
      },
    },
    {
      dataCy: 'menu-item-delete-button',
      name: 'Delete',
      actionOnClick: handleOpenInfoModal,
    },
  ];

  const handleDeleteTemplate = async () => {
    try {
      await deleteTemplate(reportTemplatesData.id).unwrap();
      refetchTemplates();
      handleCloseInfoModal();
      successNotify(t('SuccessfullyDeleted'));
    } catch (error) {
      handleOpenErrorModal();
    }
  };

  return (
    <>
      <ErrorModal
        errorMessage={errorData?.error?.message}
        description={errorData?.error?.details}
        isOpen={isErrorModalOpen}
        handleClose={handleCloseErrorModal}
      />
      <WarningModal
        warningMessage={t('AreYouSure')}
        description={t('DoYouWantToDeleteReport', { 0: reportTemplatesData.name })}
        isOpen={isOpenInfoModal}
        handleClose={handleCloseInfoModal}
        handleConfirm={handleDeleteTemplate}
      />
      <GenerateTemplateModal
        handleClose={handleCloseGenerateTemplateModal}
        isOpen={isOpenGenerateTemplateModal}
        reportId={id}
        reportTemplatesData={reportTemplatesData}
      />

      <TableRow dataCy="report-templates-table-row">
        <TableData dataCy="table-data-role-name">{name}</TableData>
        <TableData dataCy="table-data-role-type">{visualizationType}</TableData>
        <TableData dataCy="table-data-role-creator-name">{creatorName}</TableData>
        <TableData dataCy="table-data-role-date">{format(parseISO(creationTime), DAY_FORMAT_WITH_SLASHES)}</TableData>
        <TableData>
          <DropdownMenu elements={menuItemsAction} />
        </TableData>
      </TableRow>
    </>
  );
};

export default ReportTemplatesRow;
